import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { ProductionJobStatus } from '../../../models/review/production-job.model';
import { DocBates } from '../../../models/doc-bates.model';
import { ProductionJobDocsService } from '../../../services/production-job-docs.service';
import { ByteSize } from '../../../models/byte-size.model';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { CodingFieldsService } from '../../../services/coding-fields.service';
import { UserConfigsService } from '../../../services/user-configs.service';
import { Role } from '../../../models/role.model';
import { ProductionJobShared, SharedExportSettingsReturnData } from '../../../models/production-job-shared.model';
import { UserResource } from '../../../models/resource/user.resource';
import { FilenameValidator } from '../../../validators/filename-validator';
import { MatDialog } from '@angular/material/dialog';
import { SelectFieldsDialogComponent } from '../../dialogs/select-fields/select-fields.component';
import { FieldLayoutViewModel } from 'src/app/models/field/field-layout-view-model.model';
import { FieldLayoutViewModelService } from '../../../services/field-layout-view-model.service';
import { UserManager } from '../../../managers/user.manager';
import { ProductionJobView } from '@models/ui/production-job-view.model';
import { ProductionJobSdk } from 'src/app/sdks/production-job.sdk';
import IMask from 'imask';
import { SharedProductionDownloadSettingsComponent } from '../dialogs/shared-production-downloads/shared-production-download-settings.component';
import { SharedExportSdk } from '@sdks/shared-export.sdk';
import { forkJoin, map, of, switchMap, take, takeUntil } from 'rxjs';
import { BaseComponent } from '../../base.component';
import { ProductionJobAuditLogComponent } from '../dialogs/production-job-audit-log/production-job-audit-log.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AppConfigService } from '@services/app-config.service';
import { MultiSort, SelectSortComponent } from '@dialogs/select-sort/select-sort.component';
import { CustomCodingFieldsManager } from '@models/review/custom-coding-fields-manager';
import { BuiltInFieldsService } from '@services/built-in-fields.service';
import { SavedSortSdk } from '@sdks/saved-sort.sdk';
import { ProductionSortProcessor } from '@models/review/production-sort-processor';
import { MultiSortView } from '@models/ui/multi-sort-view.model';

@Component({
    selector: 'lex-production-job-details',
    templateUrl: './production-job-details.component.html',
    styleUrls: ['./production-job-details.component.scss'],
    standalone: false
})
export class ProductionJobDetailsComponent extends BaseComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
	@Input() productionJob: ProductionJobView;
	@Output() formStatus = new EventEmitter<boolean>();
	@ViewChild('batesBaseNumberStartInput') batesBaseNumberStartInput!: ElementRef;

	public sortProcessor: ProductionSortProcessor;
	public optionsFormGroup: UntypedFormGroup;
	public productionJobRunStatus = ProductionJobStatus;
	public producedSizeFormatted: string;
	public withheldSizeFormatted: string;
	public totalSizeFormatted: string;
	public sharedProductionExport: ProductionJobShared;
	public producedDocCount: number;
	public withheldDocCount: number;
	public partiallyPrivilegedDocCount: number;
	public loadFileFieldsCustom: number = 0;
	public sortData: MultiSortView;

	get Role(): typeof Role { return Role; }

	public displayColumns = ['label', 'produced', 'partiallyPrivileged', 'withheld', 'total'];
	public resultTableRows: { label: string, produced: any, partiallyPrivileged: any, withheld: any, total: any }[];
	public maskRef: any;
	public downloadLink: string = null;
	public sharedProductionLoaded: boolean = false;
	
	private users: UserResource[];
	private totalDocCount: number;

	private readonly ccfManager: CustomCodingFieldsManager;
	
	constructor(
		public userConfigService: UserConfigsService,
		public fieldLayoutViewModelService: FieldLayoutViewModelService,
		private codingFieldsService: CodingFieldsService,
		private productionDocsService: ProductionJobDocsService,
		private sharedExportSdk: SharedExportSdk,
		private userManager: UserManager,
		private formBuilder: UntypedFormBuilder,
		private codingFieldService: CodingFieldsService,
		private matDialog: MatDialog,
		private productionJobSdk: ProductionJobSdk,
		private cdr: ChangeDetectorRef,
		private snackBar: MatSnackBar,
		private appConfigService: AppConfigService,
		private builtInFieldsService: BuiltInFieldsService,
		private savedSortSdk: SavedSortSdk
	) {
		super();
		this.ccfManager = new CustomCodingFieldsManager(this.codingFieldsService);
		this.sortProcessor = new ProductionSortProcessor(builtInFieldsService, this.ccfManager);

		this.optionsFormGroup = this.formBuilder.group({
			batesBasePrefix: new UntypedFormControl('', [FilenameValidator]),
			batesBaseNumberStart: new UntypedFormControl(1, [Validators.required, Validators.min(1), Validators.max(this.maxBaseNumberStart())]),
			batesBaseNumberDigits: new UntypedFormControl(''),
			customDesignation: new UntypedFormControl(''),
			isBatesStampPages: new UntypedFormControl(''),
			isCreateTiff: new UntypedFormControl(''),
			isIncludeNativeEmails: new UntypedFormControl('')
		});
		this.optionsFormGroup.get('batesBaseNumberDigits').valueChanges.subscribe(() => {
			const max = this.maxBaseNumberStart();
			const batesBaseNumberStartControl = this.optionsFormGroup.get('batesBaseNumberStart');
			batesBaseNumberStartControl.setValidators([Validators.required, Validators.min(1), Validators.max(max)]);
			batesBaseNumberStartControl.updateValueAndValidity();
			if (this.maskRef)
				this.maskRef.destroy();
			this.initializeMask();
			this.optionsFormGroup.updateValueAndValidity();
		});
	}

	public ngOnInit() {
		this.ccfManager.retrieve();
		this.userManager.getProductionJobUsers().subscribe(data => this.users = data);
				this.optionsFormGroup.statusChanges.subscribe(() => {
			this.formStatus.emit(this.optionsFormGroup.valid);
		});
	}

	public ngOnChanges(changes: SimpleChanges): void {
		if (changes && changes.productionJob) {
			this.sharedProductionLoaded = false;
			this.downloadLink = null;
			this.sortData = null;
			// Reset counts before loading them
			this.setJobValuesAndFillResultsTable(null, null, null);
			// Use the LexbeDoc model to reflect how the document is currently coded.
			if (this.productionJob.status === ProductionJobStatus.NeedValidation || this.productionJob.status === ProductionJobStatus.PreRun) {
				this.productionDocsService.getDocCountsByProductionJobId(this.productionJob.productionJobId)
					.subscribe(data => this.setJobValuesAndFillResultsTable(data.producedDocCount, data.partiallyPrivilegedDocCount, data.withheldDocCount));
			}
			else if (this.productionJob.status === ProductionJobStatus.Queued || this.productionJob.status === ProductionJobStatus.Running || this.productionJob.status === ProductionJobStatus.PostRun) {
				// Use the ProductionJobDoc model to reflect how the document was coded at the time the production was run.
				this.setJobValuesAndFillResultsTable(
					this.productionJob.producedDocCount,
					this.productionJob.partiallyPrivilegedDocCount,
					this.productionJob.withheldDocCount);
			}

			this.sharedExportSdk.getProductionJobShared(this.productionJob.productionJobId).pipe(
				takeUntil(this.destroy$)
			).subscribe((sharedJob) => {
				this.sharedProductionLoaded = true;
				if (sharedJob?.sharedId) {
					this.sharedProductionExport = sharedJob;
					this.downloadLink = `${this.appConfigService.getConfig().webApi}/download-production/${this.sharedProductionExport.sharedId}`;
				}
				else {
					this.sharedProductionExport = new ProductionJobShared();
					this.sharedProductionExport.productionJobId = this.productionJob.productionJobId;
				}
			});
			const builtInFields$ = this.builtInFieldsService.getFields().pipe(
				take(1)
			);
			const ccfReady$ = this.ccfManager.all$.pipe(
				map(all => all && all.size > 0 ? all : null),
				take(1),
				takeUntil(this.destroy$)
			);
			forkJoin([builtInFields$, ccfReady$]).pipe(
				switchMap(() => {
					if (this.productionJob.sortId) {
						return this.savedSortSdk.getSavedSort(this.productionJob.sortId).pipe(
							map(savedSort => ({
								savedSort
							}))
						);
					} else {
						return of({
							savedSort: null
						});
					}
				}),
				takeUntil(this.destroy$)
			).subscribe(({ savedSort }) => {
				if (savedSort) {
					this.sortData = this.sortProcessor.generateMultiSortView(savedSort.sortDetails);
				} else {
					this.sortData = this.sortProcessor.createMultiSortFromLegacySort(this.productionJob);
				}

			});

			if (changes.productionJob?.currentValue && changes.productionJob?.currentValue.productionJobId !== changes.productionJob?.previousValue?.productionJobId)
				this.optionsFormGroup.reset(this.getJobOptionValues());
			this.formStatus.emit(this.optionsFormGroup.valid);
			this.checkIsCustom();
		}
		if (!this.maskRef) this.initializeMask();
	}

	public ngAfterViewInit() {
		this.initializeMask();
	}

	public ngOnDestroy() {
		if (this.maskRef) {
			this.maskRef.destroy();
		}
	}

	public selectSort() {
		const dialogRef = this.matDialog.open(SelectSortComponent, {
			width: '600px',
			data: {
				isProductionSort: true,
				currentSort: this.sortData,
				ccfManager: this.ccfManager
			}
		});

		dialogRef.afterClosed().subscribe((sortData: MultiSort) => {
			if (!sortData) return;
			this.sortData = this.sortProcessor.generateMultiSortView(sortData);
			this.savedSortSdk.createSavedSort(sortData).subscribe((data) => {
				if (data) {
					const jobPatch = {};
					jobPatch['sortId'] = data.sortId;
					this.productionJobSdk.patch(this.productionJob.productionJobId, jobPatch).subscribe();
					this.updateCurrentProductionFromPatchValues(jobPatch);
					this.sortData = this.sortProcessor.generateMultiSortView(data.sortDetails);
				}
			});
		});
	}

	private checkIsCustom() {
		this.productionJobSdk
			.getLoadfileFieldLayoutIsCustom(this.productionJob.productionJobId)
			.subscribe(isCustom => {
				this.loadFileFieldsCustom = isCustom ? 1 : 0;
			});
	}

	public maxBaseNumberStart(): number { return Math.pow(10, this.optionsFormGroup?.get('batesBaseNumberDigits').value) - 1; }

	public getExampleBateString(): string {
		const prefix = this.optionsFormGroup.get('batesBasePrefix');
		const numberStart = this.optionsFormGroup.get('batesBaseNumberStart');
		const numberDigits = this.optionsFormGroup.get('batesBaseNumberDigits');
		return DocBates.batesStampString(prefix.value, numberStart.value, numberDigits.value);
	}

	public isProductionReadOnly(): boolean {
		if (this.userConfigService.inRoles(Role.EditFeatureSet, Role.Administrator))
			return this.productionJob.status === this.productionJobRunStatus.Queued || this.productionJob.status === this.productionJobRunStatus.Running || this.productionJob.status === this.productionJobRunStatus.PostRun;

		return true;
	}

	public getUserName(userId: string): string {
		if (!this.users) return '';
		if (this.users.findIndex(u => u.id === userId) < 0) return '';
		return this.users.find(u => u.id == userId).userName;
	}

	public updateJob(isUpdateComment: boolean = false) {
		this.optionsFormGroup.updateValueAndValidity();
		if (!this.optionsFormGroup.valid)
			return;
		const jobPatch = {};
		const keyValueArr = Object.entries(this.optionsFormGroup.controls).filter(value => value[1].dirty);
		keyValueArr.forEach(kvp => {
			if (kvp[0] === 'isCreateTiff')
				jobPatch[kvp[0]] = kvp[1].value === 0 ? false : true;
			else
				jobPatch[kvp[0]] = kvp[1].value;
		});

		if (isUpdateComment)
			jobPatch['comments'] = this.productionJob.comments;

		if (Object.keys(jobPatch).length === 0)
			return;

		this.productionJobSdk.patch(this.productionJob.productionJobId, jobPatch).subscribe();
		this.updateCurrentProductionFromPatchValues(jobPatch);
	}

	private getJobOptionValues() {
		return {
			batesBasePrefix: this.productionJob.batesBasePrefix,
			batesBaseNumberStart: this.productionJob.batesBaseNumberStart,
			batesBaseNumberDigits: this.productionJob.batesBaseNumberDigits,
			customDesignation: this.productionJob.customDesignation,
			isBatesStampPages: this.productionJob.isBatesStampPages,
			isCreateTiff: this.productionJob.isCreateTiff ? 1 : 0,
			isIncludeNativeEmails: this.productionJob.isIncludeNativeEmails
		};
	}

	private updateCurrentProductionFromPatchValues(jobPatch) {
		const keyValueArr = Object.entries(jobPatch);
		keyValueArr.forEach(kvp => {
			if (kvp[0] == 'isCreateTiff')
				this.productionJob.isCreateTiff = kvp[1] === 0 ? false : true;
			else
				this.productionJob[kvp[0]] = kvp[1];
		});
	}

	public openSharedSettings(): void {
		const dialogRef = this.matDialog.open(SharedProductionDownloadSettingsComponent,
			{
				data: {
					sharedDetails: this.sharedProductionExport
				}
			});

		dialogRef.afterClosed().subscribe((sharedJobSettingsData: SharedExportSettingsReturnData) => {
			if (!sharedJobSettingsData)
				return;

			if (sharedJobSettingsData.isGenerateLink) {
				this.sharedProductionLoaded = false;
				this.sharedProductionExport = sharedJobSettingsData.sharedProductionJob;
				this.sharedExportSdk.createProductionShared(this.sharedProductionExport).subscribe((data) => {
					this.sharedProductionLoaded = true;

					this.sharedProductionExport = data;
					this.downloadLink = `${this.appConfigService.getConfig().webApi}/download-production/${this.sharedProductionExport.sharedId}`;
					this.snackBar.open('Shared access link generated.', 'Close', {
						duration: 3000,
						verticalPosition: 'bottom',
						horizontalPosition: 'center',
					});
				});
			} else if (sharedJobSettingsData.isRemoveShare) {
				this.sharedProductionLoaded = false;
				this.sharedExportSdk.deleteProductionShared(sharedJobSettingsData.sharedProductionJob.sharedId).subscribe((data) => {
					if (data) {
						this.sharedProductionExport = new ProductionJobShared();
						this.sharedProductionExport.productionJobId = this.productionJob.productionJobId;
						this.downloadLink = null;
						this.snackBar.open('Remove shared access link.', 'Close', {
							duration: 3000,
							verticalPosition: 'bottom',
							horizontalPosition: 'center',
						});
					}
					this.sharedProductionLoaded = true;
				});
			}
		})
	}

	public copySharedLink() {
		if (!this.sharedProductionExport.sharedId) return '';
		return this.downloadLink;
	}

	public openProductionAuditLog(): void {
		this.matDialog.open(ProductionJobAuditLogComponent,
			{
				data: {
					productionJobId: this.productionJob.productionJobId
				}
			});
	}

	private setJobValuesAndFillResultsTable(producedCount?: number, partiallyPrivilegedCount?: number, withheldCount?: number) {
		this.producedDocCount = producedCount;
		this.partiallyPrivilegedDocCount = partiallyPrivilegedCount;
		this.withheldDocCount = withheldCount;
		this.totalDocCount = this.producedDocCount + this.partiallyPrivilegedDocCount + this.withheldDocCount;

		if (this.productionJob.status === ProductionJobStatus.PostRun) {
			const total = new ByteSize(this.productionJob.docSizeProduced + this.productionJob.docSizeWithheld);
			this.totalSizeFormatted = total.sizeInGigabytes;

			const produced = new ByteSize(this.productionJob.docSizeProduced);
			this.producedSizeFormatted = produced.sizeInGigabytes;

			const withheld = new ByteSize(this.productionJob.docSizeWithheld);
			this.withheldSizeFormatted = withheld.sizeInGigabytes;

			// Clear existing rows to display table.
			this.resultTableRows = [];
			this.resultTableRows.push({ label: 'Count', produced: this.producedDocCount, partiallyPrivileged: this.partiallyPrivilegedDocCount, withheld: this.withheldDocCount, total: this.totalDocCount });
			this.resultTableRows.push({ label: 'Size (GB)', produced: this.producedSizeFormatted, partiallyPrivileged: '', withheld: this.withheldSizeFormatted, total: this.totalSizeFormatted });
			this.resultTableRows.push({ label: 'Range', produced: this.productionJob.batesRangeProduction, partiallyPrivileged: '', withheld: this.productionJob.batesRangeWithheld, total: '' });
		}
		else {
			this.totalSizeFormatted = '';
			this.producedSizeFormatted = '';
			this.withheldSizeFormatted = '';
		}
	}

	public showChooseLoadFileFields(canEdit) {
		this.productionJobSdk
			.getLoadfileFieldLayout(this.productionJob.productionJobId)
			.subscribe(fieldLayout => {
				const dialogRef = this.matDialog.open(SelectFieldsDialogComponent,
					{
						data: {
							view: JSON.parse(JSON.stringify(fieldLayout)),
							isSearch: false,
							isLog: false,
							isProduction: true,
							canEdit: canEdit
						}
					});

				dialogRef.afterClosed().subscribe((data: FieldLayoutViewModel) => {
					if (!data) return;
					this.fieldLayoutViewModelService.updateFieldLayout(
						data)
						.subscribe(() => {
							this.checkIsCustom();
						});
				});
			});
	}

	public syncMask(): void {
		// There is an issue where when the form is updated with user input, it is always passed to the form control, bypassing the mask?
		// To avoid this, update the form with the masked value when the user input is out of sync with the mask.
		const v = this.optionsFormGroup.get('batesBaseNumberStart').value
		if (this.maskRef && this.maskRef?.value != v) {
			this.optionsFormGroup.get('batesBaseNumberStart').setValue(this.maskRef.value);
		}
		this.updateJob();
	}

	private initializeMask(): void {
		if (!this.batesBaseNumberStartInput && !this.batesBaseNumberStartInput?.nativeElement)
			return;
		const max = this.maxBaseNumberStart();
		const maxLength = this.optionsFormGroup.get('batesBaseNumberDigits').value;
		this.maskRef = IMask(this.batesBaseNumberStartInput.nativeElement, {
			mask: IMask.MaskedRange,
			from: 1,
			to: max,
			maxLength: maxLength,
			autofix: true,
			commit: (value,) => {
				this.optionsFormGroup.get('batesBaseNumberStart').setValue(value);
			}
		});

		if (this.optionsFormGroup.get('batesBaseNumberStart') && this.optionsFormGroup.valid) {
			this.maskRef.value = this.optionsFormGroup.get('batesBaseNumberStart').value.toString();
			this.maskRef.updateValue();
		}
		this.cdr.detectChanges();
	}
}
